import React from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { motion } from "framer-motion";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

// List of services with keys for translation and image paths
const services1 = [
  {
    key: "realEstate",
    image: "image/RD.png",
    align: "left",
  },
  {
    key: "propertyManagement",
    image: "image/PM.png",
    align: "right",
  },
  {
    key: "construction",
    image: "image/CM.png",
    align: "left",
  },
];

// ServicePanel component with 3D effects
const ServicePanel = ({ title, description, image, align }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box
      sx={{
        position: "relative",
        height: { xs: "90vh", sm: "100vh" },
        display: "flex",
        alignItems: "center",
        justifyContent: align === "left" ? "flex-start" : "flex-end",
        backgroundImage: `url(${image})`,
        backgroundAttachment: "fixed", // PARALLAX SCROLL EFFECT
        backgroundSize: "cover",
        backgroundPosition: "center",
        overflow: "hidden",
        px: 2,
        py: 4,
        perspective: "1000px", // Enable 3D transform context
        "::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(to right, rgba(0,0,0,0.6), rgba(0,0,0,0.3))",
          zIndex: 1,
        },
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9, rotateY: align === "left" ? -10 : 10 }}
        whileInView={{ opacity: 1, scale: 1, rotateY: 0 }}
        transition={{ duration: 0.9, ease: "easeOut" }}
        viewport={{ once: true }}
        style={{
          zIndex: 2,
          maxWidth: isMobile ? "100%" : "50%",
          background: "rgba(255,255,255,0.95)",
          padding: "2rem",
          borderRadius: "1rem",
          boxShadow:
            "0 15px 40px rgba(0,0,0,0.4), inset 0 0 10px rgba(255,255,255,0.2)", // DEPTH SHADOWS
          transformStyle: "preserve-3d",
          backdropFilter: "blur(8px)",
          transform: "translateZ(20px)",
        }}
      >
        <Typography
          variant={isMobile ? "h5" : "h4"}
          fontWeight="bold"
          gutterBottom
          sx={{
            textShadow: "1px 1px 2px rgba(0,0,0,0.3)", // LIGHT GLOW
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ lineHeight: 1.6 }}
        >
          {description}
        </Typography>
      </motion.div>
    </Box>
  );
};

// Main page component
const CreativeServicesPage = () => {
  const { t, i18n } = useTranslation();
  const direction = i18n.language === "ar" ? "rtl" : "ltr";

  return (
    <Box dir={direction} sx={{ width: "100%" }}>
      {services1.map((service, index) => (
        <ServicePanel
          key={index}
          title={t(`services1.${service.key}.title`)}
          description={t(`services1.${service.key}.description`)}
          image={service.image}
          align={service.align}
        />
      ))}
    </Box>
  );
};

export default CreativeServicesPage;
